import { Link } from 'react-router-dom';
import { Carte, Pastille } from '../ui/Primitives';
import { LigneModeleDepose } from '../documents/LigneModeleDepose';
import { useDeposerModele, type VueParametrage } from '../../lib/useParametrage';

const TONS_ORIGINE: Record<string, string> = {
  FORMULAIRE_OFFICIEL: 'info',
  MODELE_REPRODUIT: 'mint',
  CHARTE: 'neutral',
};

/**
 * Modèles de documents (TF-435) : la direction dépose les modèles des documents
 * pédagogiques remis à l'entrée, comme le pôle pédagogique dans « Documents
 * pédagogiques » (TF-477) ; le reste est inventorié avec son origine, pour savoir ce
 * qui se change à l'écran et ce qui demande une évolution.
 */
export function OngletModeles({ modeles }: { modeles: VueParametrage['modeles'] }): JSX.Element {
  const deposer = useDeposerModele();
  const cerfaDeposes = modeles.cerfa.filter((cerfa) => cerfa.depose).length;
  return (
    <div className="space-y-6">
      <Carte titre="Documents remis à l’entrée · modèles déposés">
        <p className="px-4 py-3 text-xs text-codialis-gray sm:px-6">
          {modeles.origines.MODELE_DEPOSE.detail} Un modèle commun aux deux antennes, en PDF, déposé ici ou par le pôle
          pédagogique dans{' '}
          <Link to="/ped-documents" className="font-semibold text-codialis-sky hover:underline">
            « Documents pédagogiques »
          </Link>
          , où se déposent aussi les fiches REAC par formation. Un document déjà remis garde la copie signée : un nouveau
          modèle ne réécrit rien.
        </p>
        {modeles.deposes.map((modele) => (
          <LigneModeleDepose
            key={modele.code}
            libelle={modele.libelle}
            depots={modele}
            sansModele="Tant qu’il manque, ce document ne part pas à la signature."
            deposer={(fichier) =>
              deposer.mutateAsync({ code: modele.code, fichier }).then((reponse) => reponse.message)
            }
          />
        ))}
      </Carte>

      <Carte titre="Formulaires officiels">
        <p className="px-4 py-3 text-xs text-codialis-gray sm:px-6">
          {modeles.origines.FORMULAIRE_OFFICIEL.detail}{' '}
          <Link to="/adm-import" className="font-semibold text-codialis-sky hover:underline">
            Ouvrir l’import
          </Link>
        </p>
        <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-codialis-navy">CERFA pré-remplis par formation et par antenne</div>
            <div className="text-[11px] text-codialis-gray">
              Sans formulaire d’antenne, le CERFA vierge livré sert de repli.
            </div>
          </div>
          <Pastille
            texte={`${cerfaDeposes} sur ${modeles.cerfa.length} déposé${cerfaDeposes > 1 ? 's' : ''}`}
            ton={cerfaDeposes === modeles.cerfa.length ? 'success' : 'warning'}
          />
        </div>
        <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-codialis-navy">Formulaire de résiliation du contrat</div>
            <div className="text-[11px] text-codialis-gray">
              {modeles.formulaireRupture.depose
                ? 'Formulaire déposé par l’import.'
                : 'Formulaire du client livré avec la plateforme.'}
            </div>
          </div>
          <Pastille texte={modeles.formulaireRupture.depose ? 'Déposé' : 'Livré'} ton="success" />
        </div>
      </Carte>

      <Carte titre="Documents composés par la plateforme" sansBordure>
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                {['Document', 'Mise en page', 'Modèle', 'Produit dans'].map((colonne) => (
                  <th
                    key={colonne}
                    className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                  >
                    {colonne}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {modeles.produits.map((document) => (
                <tr key={document.libelle}>
                  <td className="px-4 py-3 font-semibold text-codialis-navy">{document.libelle}</td>
                  <td className="px-4 py-3">
                    <Pastille texte={document.origineLibelle} ton={TONS_ORIGINE[document.origine] ?? 'neutral'} />
                  </td>
                  <td className="px-4 py-3 text-xs text-codialis-gray">{document.source}</td>
                  <td className="px-4 py-3 text-xs text-codialis-gray">{document.ou}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-gray-100 px-4 py-3 text-[11px] text-codialis-gray sm:px-6">
          {modeles.origines.MODELE_REPRODUIT.detail} {modeles.origines.CHARTE.detail}
        </p>
      </Carte>
    </div>
  );
}
